import { Check, X, Minus } from "lucide-react";
import { Link } from "react-router-dom";
import { Shell, H2, Reveal } from "./parts";
import { draftPath } from "./DraftLayout";

const ROWS = [
  { what: "Managed to", us: "Contribution margin after ad spend, returns and fees", them: "Platform ROAS and CPA" },
  { what: "Creative", us: "In-house team, new entity IDs in the account every week, included with paid traffic", them: "Outsourced or extra, delivered in batches" },
  { what: "Who is in your account", us: "The people running your ads, creative and email, in one Slack channel with you, daily", them: "An account manager relaying messages" },
  { what: "Reporting", us: "A weekly profit sheet: spend, revenue, margin, contribution", them: "A monthly dashboard of platform metrics" },
  { what: "Retention", us: "Revenue per recipient and share of revenue, list run like a P&L line", them: "Open rates and a promo calendar" },
  { what: "Skin in the game", us: "We run our own brands and spend our own money on ads every day", them: "Agency only" },
  { what: "When to cut", us: "We will tell you to spend less when the margin says so. Clean-Eez started with a cut.", them: "More spend is always the answer" },
];

export function Compare() {
  return (
    <Shell tone="navy" testId="compare">
      <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-pink">Side by side</div>
      <H2 light className="mt-3">How this is different from the agency you have now.</H2>
      <p className="mt-3 max-w-2xl text-base text-white/70 md:text-lg">Not a knock on anyone. Just the things we do differently, so you can decide fast whether this is for you.</p>
      <div className="mt-12 overflow-hidden rounded-3xl border border-white/15 bg-white/[0.02] shadow-2xl backdrop-blur-md">
        <div className="hidden grid-cols-[1fr_1.4fr_1.1fr] border-b border-white/10 bg-white/[0.06] text-[11px] font-semibold uppercase tracking-[0.14em] text-white/60 md:grid">
          <div className="px-5 py-4"> </div>
          <div className="px-5 py-4 text-white">Smart Marketer Agency</div>
          <div className="px-5 py-4">A typical agency</div>
        </div>
        {ROWS.map((r, i) => (
          <Reveal key={r.what} delay={i * 0.04} className="grid grid-cols-1 border-t border-white/10 text-sm first:border-t-0 md:grid-cols-[1fr_1.4fr_1.1fr] md:first:border-t">
            <div className="px-5 pt-5 text-[11px] font-semibold uppercase tracking-[0.14em] text-pink md:py-4 md:text-sm md:normal-case md:tracking-normal md:font-bold md:text-white">{r.what}</div>
            <div className="flex flex-col gap-2 px-5 py-4 text-white/90 md:flex-row md:gap-3 md:border-x md:border-white/10 md:bg-white/[0.04]">
              <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-white/50 md:hidden">Smart Marketer Agency</span>
              <div className="flex gap-3">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-green/20 text-green ring-1 ring-green/40"><Check size={12} strokeWidth={3} /></span>
                <span>{r.us}</span>
              </div>
            </div>
            <div className="flex flex-col gap-2 px-5 pb-5 text-white/60 md:flex-row md:gap-3 md:py-4">
              <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-white/40 md:hidden">A typical agency</span>
              <div className="flex gap-3">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white/10 text-white/60"><Minus size={12} strokeWidth={3} /></span>
                <span>{r.them}</span>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
      <div className="mt-8 text-sm text-white/60">
        Want the long version? <Link to={draftPath("/how-we-work")} data-testid="compare-how-we-work" className="font-bold text-white hover:underline">How we work</Link>
      </div>
    </Shell>
  );
}

const FAQS = [
  { q: "Who is this for?", a: "DTC brands spending $30k a month or more on ads, or doing $150k a month or more in revenue. Below that, the retainer eats the profit we are supposed to add, and we will say so. Our mastermind is the better fit until then." },
  { q: "Who actually works on my account?", a: "A Paid Performance Specialist, a creative strategist with our in-house designers and editors, and a retention lead if you take email and SMS, all in one Slack channel with you. No account manager in between. Pep and the department heads review every account weekly." },
  { q: "How fast can we start?", a: "Fit check, a call with Pep, then a second call where we present the 90-day plan. Once the proposal is signed we are live within 10 business days, with the first creative drop inside that window." },
  { q: "Which channels do you run?", a: "Meta and Google are the core for most brands. TikTok, AppLovin and ChatGPT come in where they fit the product and the margin, tested small first. Retention covers email and SMS in Klaviyo or your platform of choice." },
  { q: "How do you report?", a: "One profit sheet per client, updated weekly: spend, revenue, margin, contribution. Platform attribution, a third-party view and blended numbers side by side, so you see what the ads added to the business, not just to the dashboard." },
  { q: "What if the numbers say we should spend less?", a: "Then we tell you, and we cut. Clean-Eez started with a spend reduction because the first job was to stop losing money. Revenue went from $400k to $1.2M a month within the year after that." },
];

export function Faq() {
  return (
    <Shell testId="faq">
      <div className="grid gap-12 lg:grid-cols-[1fr_1.6fr]">
        <div>
          <H2>Questions we get on the first call.</H2>
          <p className="mt-4 text-base text-navy/70 md:text-lg">If yours is not here, ask it on the discovery call or email <a href="mailto:pep@smartmarketer.com" className="font-bold text-link">pep@smartmarketer.com</a>.</p>
        </div>
        <div className="divide-y divide-mist border-y border-mist">
          {FAQS.map((f, i) => (
            <details key={f.q} data-testid={`faq-${i}`} className="group py-5">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-6 text-left text-base font-bold text-navy md:text-lg">
                {f.q}
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-mist text-navy transition-transform group-open:rotate-45"><X size={14} className="rotate-45" /></span>
              </summary>
              <p className="mt-3 max-w-2xl text-base leading-relaxed text-navy/80">{f.a}</p>
            </details>
          ))}
        </div>
      </div>
    </Shell>
  );
}
